import { useAutoneTranslation } from '@autone/translations';
import {
  AdvancedFiltersDefaultInputs,
  createAdvancedFilterConfig,
} from '@autone/ui';
import { useCallback, useMemo } from 'react';

import { type APP_NAME } from '../../../../consts';
import { type AssortmentAdvancedFiltersTranslationsMap } from '../../../../hooks/use-assortment-advanced-filters';
import { type GetAssortmentProductLocationsAdvancedFilterConditions } from '../../../../redux/assortment';

const NUMERIC_FIELD_OPERATORS = ['eq', 'gt', 'lt', 'ge', 'le'] as const;

export const getProductLocationsTableAdvancedFilterConfig = ({
  fields,
  operators,
}: AssortmentAdvancedFiltersTranslationsMap) =>
  createAdvancedFilterConfig<GetAssortmentProductLocationsAdvancedFilterConditions>(
    {
      fieldOptions: {
        lifetime_sales_units: fields.lifetime_sales_units,
        lifetime_sales_value: fields.lifetime_sales_value,
        first_sale_date: fields.first_sale_date,
        latest_sale_date: fields.latest_sale_date,
        initial_allocation: fields.initial_allocation,
        inventory_units: fields.inventory_units,
        parent_location_inventory_units: fields.parent_location_inventory_units,
        is_assorted: fields.is_assorted,
        assorted_start_date: fields.assorted_start_date,
        assorted_end_date: fields.assorted_end_date,
        parent_location_pending_from_production_units:
          fields.parent_location_pending_from_production_units,
        pending_from_production_units: fields.pending_from_production_units,
      },
      operatorOptions: {
        eq: operators.eq,
        gt: operators.gt,
        lt: operators.lt,
        ge: operators.ge,
        le: operators.le,
        is: operators.is,
      },
      fieldConfigs: {
        lifetime_sales_units: {
          operators: NUMERIC_FIELD_OPERATORS,
          renderInput: ({ value, onValueChange }) => (
            <AdvancedFiltersDefaultInputs.Number
              min={0}
              value={value}
              onChange={(val) =>
                onValueChange(val !== null ? Number(val) : undefined)
              }
            />
          ),
        },
        lifetime_sales_value: {
          operators: NUMERIC_FIELD_OPERATORS,
          renderInput: ({ value, onValueChange }) => (
            <AdvancedFiltersDefaultInputs.Number
              min={0}
              value={value}
              onChange={(val) =>
                onValueChange(val !== null ? Number(val) : undefined)
              }
            />
          ),
        },
        first_sale_date: {
          operators: NUMERIC_FIELD_OPERATORS,
          renderInput: ({ value, onValueChange }) => (
            <AdvancedFiltersDefaultInputs.SingleDate
              value={value}
              onValueChange={onValueChange}
            />
          ),
        },
        latest_sale_date: {
          operators: NUMERIC_FIELD_OPERATORS,
          renderInput: ({ value, onValueChange }) => (
            <AdvancedFiltersDefaultInputs.SingleDate
              value={value}
              onValueChange={onValueChange}
            />
          ),
        },
        initial_allocation: {
          operators: NUMERIC_FIELD_OPERATORS,
          renderInput: ({ value, onValueChange }) => (
            <AdvancedFiltersDefaultInputs.Number
              min={0}
              value={value}
              onChange={(val) =>
                onValueChange(val !== null ? Number(val) : undefined)
              }
            />
          ),
        },
        inventory_units: {
          operators: NUMERIC_FIELD_OPERATORS,
          renderInput: ({ value, onValueChange }) => (
            <AdvancedFiltersDefaultInputs.Number
              min={0}
              value={value}
              onChange={(val) =>
                onValueChange(val !== null ? Number(val) : undefined)
              }
            />
          ),
        },
        parent_location_inventory_units: {
          operators: NUMERIC_FIELD_OPERATORS,
          renderInput: ({ value, onValueChange }) => (
            <AdvancedFiltersDefaultInputs.Number
              min={0}
              value={value}
              onChange={(val) =>
                onValueChange(val !== null ? Number(val) : undefined)
              }
            />
          ),
        },
        assorted_start_date: {
          operators: NUMERIC_FIELD_OPERATORS,
          renderInput: ({ value, onValueChange }) => (
            <AdvancedFiltersDefaultInputs.SingleDate
              value={value}
              onValueChange={onValueChange}
            />
          ),
        },
        assorted_end_date: {
          operators: NUMERIC_FIELD_OPERATORS,
          renderInput: ({ value, onValueChange }) => (
            <AdvancedFiltersDefaultInputs.SingleDate
              value={value}
              onValueChange={onValueChange}
            />
          ),
        },
        parent_location_pending_from_production_units: {
          operators: NUMERIC_FIELD_OPERATORS,
          renderInput: ({ value, onValueChange }) => (
            <AdvancedFiltersDefaultInputs.Number
              min={0}
              value={value}
              onChange={(val) =>
                onValueChange(val !== null ? Number(val) : undefined)
              }
            />
          ),
        },
        pending_from_production_units: {
          operators: NUMERIC_FIELD_OPERATORS,
          renderInput: ({ value, onValueChange }) => (
            <AdvancedFiltersDefaultInputs.Number
              min={0}
              value={value}
              onChange={(val) =>
                onValueChange(val !== null ? Number(val) : undefined)
              }
            />
          ),
        },
        is_assorted: {
          operators: ['is'],
          renderInput: function IsAssortedInput({ value, onValueChange }) {
            const { t } = useAutoneTranslation<typeof APP_NAME>();

            const options = useMemo(
              () => [
                {
                  id: 'assorted' as const,
                  label: t('assortment-statuses.assorted'),
                },
                {
                  id: 'unassorted' as const,
                  label: t('assortment-statuses.unassorted'),
                },
              ],
              [t],
            );

            const handleValueChange = useCallback(
              (value?: 'assorted' | 'unassorted') => {
                onValueChange(value === 'assorted');
              },
              [onValueChange],
            );

            const val = useMemo(() => {
              if (value === undefined) {
                return value;
              }
              return value ? 'assorted' : 'unassorted';
            }, [value]);

            return (
              <AdvancedFiltersDefaultInputs.SingleSelect
                options={options}
                value={val}
                onValueChange={handleValueChange}
              />
            );
          },
        },
      },
    },
  );
